import {
  email,
  required,
  useRecordContext,
  useTranslate,
  useUpdate,
  useNotify,
} from "ra-core";
import type { FocusEvent, ClipboardEventHandler } from "react";
import { useFormContext } from "react-hook-form";
import { BooleanInput } from "@/components/admin/boolean-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";

import { FormRow, FormSection, FormSections } from "../layout/FormLayout";
import { isLinkedinUrl } from "../misc/isLinkedInUrl";
import { BRUKERVALG, KUNDEVALG } from "../misc/referansevalg";
import { StatusSelector } from "../notes";
import type { Sale, Contact } from "../types";
import { Avatar } from "./Avatar";
import { ContactConsentInputs } from "./ContactConsent";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";
import { translatePersonalInfoTypeLabel } from "./contactModel.ts";

// Seksjonene i to kolonner på brede skjermer: Identitet og Stilling til
// venstre, Personlig informasjon og Diverse til høyre, så Samtykke (kap. 6.8).
export const ContactInputs = () => (
  <div className="relative flex flex-col gap-6 p-1 md:static">
    <div className="absolute top-0 right-1 md:static">
      <Avatar />
    </div>
    <FormSections columns={2}>
      <ContactIdentityInputs />
      <ContactPositionInputs />
      <ContactPersonalInformationInputs />
      <ContactMiscInputs />
      <ContactConsentInputs />
    </FormSections>
  </div>
);

const ContactIdentityInputs = () => {
  const translate = useTranslate();
  return (
    <FormSection
      title={translate("resources.contacts.field_categories.identity")}
    >
      <FormRow>
        <TextInput
          source="first_name"
          className="flex-1"
          validate={required()}
          helperText={false}
        />
        <TextInput
          source="last_name"
          className="flex-1"
          validate={required()}
          helperText={false}
        />
      </FormRow>
    </FormSection>
  );
};

const ContactPositionInputs = () => {
  const translate = useTranslate();
  return (
    <FormSection
      title={translate("resources.contacts.field_categories.position")}
    >
      <TextInput source="title" className="max-w-field-md" helperText={false} />
      <ReferenceInput
        source="company_id"
        reference="companies"
        filter={{ is_own_company: false }}
        {...KUNDEVALG}
      >
        <AutocompleteCompanyInput label="resources.contacts.fields.company_id" />
      </ReferenceInput>
    </FormSection>
  );
};

const ContactPersonalInformationInputs = () => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const personalInfoTypes = [
    {
      id: "Work",
      name: translatePersonalInfoTypeLabel("Work", translate),
    },
    {
      id: "Home",
      name: translatePersonalInfoTypeLabel("Home", translate),
    },
    {
      id: "Other",
      name: translatePersonalInfoTypeLabel("Other", translate),
    },
  ];

  // set first and last name based on email
  const handleEmailChange = (email: string) => {
    const { first_name, last_name } = getValues();
    if (first_name || last_name || !email) return;
    const [first, last] = email.split("@")[0].split(".");
    setValue("first_name", first.charAt(0).toUpperCase() + first.slice(1));
    setValue(
      "last_name",
      last ? last.charAt(0).toUpperCase() + last.slice(1) : "",
    );
  };

  const handleEmailPaste: ClipboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (e) => {
    const email = e.clipboardData?.getData("text/plain");
    handleEmailChange(email);
  };

  const handleEmailBlur = (
    e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const email = e.target.value;
    handleEmailChange(email);
  };

  return (
    <FormSection
      title={translate("resources.contacts.field_categories.personal_info")}
    >
      <ArrayInput source="email_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="email"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.contacts.fields.email")}
            validate={email()}
            onPaste={handleEmailPaste}
            onBlur={handleEmailBlur}
          />
          <SelectInput
            source="type"
            helperText={false}
            label={false}
            optionText="name"
            choices={personalInfoTypes}
            defaultValue="Work"
            className="w-24 min-w-24"
          />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="phone_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="number"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.contacts.fields.phone_number")}
          />
          <SelectInput
            source="type"
            helperText={false}
            label={false}
            optionText="name"
            choices={personalInfoTypes}
            defaultValue="Work"
            className="w-24 min-w-24"
          />
        </SimpleFormIterator>
      </ArrayInput>
      <TextInput
        source="linkedin_url"
        helperText={false}
        validate={isLinkedinUrl}
      />
    </FormSection>
  );
};

const ContactMiscInputs = () => {
  const translate = useTranslate();
  return (
    <FormSection title={translate("resources.contacts.field_categories.misc")}>
      <TextInput source="background" multiline helperText={false} />
      <BooleanInput source="has_newsletter" helperText={false} />
      <ReferenceInput
        reference="sales"
        source="sales_id"
        filter={{
          "disabled@neq": true,
        }}
        {...BRUKERVALG}
      >
        <SelectInput
          className="max-w-field-md"
          helperText={false}
          optionText={saleOptionRenderer}
          validate={required()}
        />
      </ReferenceInput>
    </FormSection>
  );
};

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

export const ContactStatusSelector = () => {
  const record = useRecordContext<Contact>();
  const [update] = useUpdate<Contact>();
  const notify = useNotify();
  if (!record) return null;

  const handleStatusChange = (nextStatus: string) => {
    if (nextStatus === record?.status) return;

    update(
      "contacts",
      {
        id: record.id,
        data: { status: nextStatus },
        previousData: record,
      },
      {
        mutationMode: "optimistic",
        onError: (error) => {
          notify(
            typeof error === "string"
              ? error
              : error?.message || "ra.notification.http_error",
            {
              type: "error",
              messageArgs: {
                _: typeof error === "string" ? error : error?.message,
              },
            },
          );
        },
      },
    );
  };

  return (
    <div className="[&_button]:w-auto">
      <StatusSelector
        status={record?.status}
        setStatus={handleStatusChange}
        triggerClassName="w-full"
      />
    </div>
  );
};
